import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import CoursePage from './CoursePage';

export const COURSE_NAME_VARIANTS = ['control', 'agentic'] as const;
export type CourseNameVariant = typeof COURSE_NAME_VARIANTS[number];

type ExperimentCoursePageProps = {
  variantKey?: CourseNameVariant;
  experimentContext?: Record<string, unknown> | null;
};

const ExperimentCoursePage = CoursePage as React.ComponentType<ExperimentCoursePageProps>;

export const normalizeCourseNameVariant = (value: unknown): CourseNameVariant | null => (
  COURSE_NAME_VARIANTS.includes(value as CourseNameVariant)
    ? value as CourseNameVariant
    : null
);

/**
 * Owns the course-name-only experiment on the canonical live bootcamp URL.
 * Deterministic review routes render CoursePage directly and do not enroll or
 * record experiment exposure.
 */
export default function CourseNameExperimentPage(): React.ReactElement {
  const location = useLocation();
  const experiment = ACTIVE_EXPERIMENTS.AI_TRADING_BOOTCAMP_COURSE_NAME;
  const featureFlagKey = experiment.featureFlag || 'ai-trading-bootcamp-course-name';
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const previewOverride = isPreviewExperimentOverrideHost()
    ? normalizeCourseNameVariant(searchParams.get('courseNameVariant'))
    : null;

  const trackingData = useMemo(() => ({
    page: 'ai_trading_bootcamp',
    surface: 'course_sales',
    source: location.pathname,
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    validVariants: COURSE_NAME_VARIANTS,
    skipFeatureFlagEvaluation: Boolean(previewOverride),
    forcedVariant: previewOverride || undefined,
    trackForcedExposure: false,
    exposure_source: previewOverride ? 'course_name_preview_override' : undefined,
  }), [location.pathname, previewOverride, searchParams]);

  const {
    variant,
    isLoading,
    isExperimentActive,
  } = useABTest(
    experiment.name,
    featureFlagKey,
    'control',
    trackingData,
  ) as {
    variant: string | boolean | null;
    isLoading: boolean;
    isExperimentActive: boolean;
  };

  const assignedVariant = normalizeCourseNameVariant(variant);
  const hasConfirmedActiveAssignment = Boolean(
    !isLoading && isExperimentActive && assignedVariant,
  );
  const resolvedVariant = previewOverride
    || (hasConfirmedActiveAssignment ? assignedVariant : null)
    || 'control';
  const shouldAttachExperiment = Boolean(previewOverride || hasConfirmedActiveAssignment);

  const experimentContext = useMemo(() => (
    shouldAttachExperiment
      ? {
        experiment_name: experiment.name,
        experiment_variant: resolvedVariant,
        feature_flag: featureFlagKey,
        [`$feature/${featureFlagKey}`]: resolvedVariant,
        is_experiment_active: hasConfirmedActiveAssignment && !previewOverride,
      }
      : null
  ), [
    experiment.name,
    featureFlagKey,
    hasConfirmedActiveAssignment,
    previewOverride,
    resolvedVariant,
    shouldAttachExperiment,
  ]);

  return <ExperimentCoursePage variantKey={resolvedVariant} experimentContext={experimentContext} />;
}
